import type { UserLite } from '@api/types/user';
import { STATUS_COLORS } from '@/constants/statusColors';
import { getUserAvatarColor } from '@/utils/collaborationUserColor';
import { getFullNameFromUserData, getUserInitials } from '@/utils/general';
import { Avatar, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Tag, { TagProps } from '../tag/Tag';
import { Content, Name, NameStack, NameWrapper, Wrapper } from './UserBadge.styles';

/**
 * Every field this badge actually reads, plus `lastActive` — which lives on
 * `UserData` rather than `UserLite` and is what `getUserAvatarColor` inspects to
 * decide the inactive treatment. All optional: call sites reach it through
 * chains like `member?.user`, so a partially-populated user is the norm.
 *
 * This replaced a bare `any`, which was a tolerated warning under the old
 * `components/` path but is a lint error inside `shared/`.
 */
export type UserBadgeUser = Partial<UserLite> & {
  lastActive?: string | number | boolean | null;
};

type UserBadgeProps = {
  signStatus?: string;
  company?: string;
  user?: UserBadgeUser;
};

export default function UserBadge({ company, signStatus, user }: UserBadgeProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const tagColor = signStatus ? (STATUS_COLORS[signStatus] as TagProps['color']) : 'gray';

  return (
    <Wrapper>
      <Avatar
        src={user?.logo}
        sx={{
          width: 28,
          height: 28,
          fontSize: 12,
        }}
        color={getUserAvatarColor(user)}
      >
        {getUserInitials(user)}
      </Avatar>
      <Content>
        <NameWrapper>
          <NameStack>
            {user && <Name>{getFullNameFromUserData(user) || user.email}</Name>}
            {user?.role === 'guest' && <Chip size='small' color='warning' label='Guest' />}
          </NameStack>
          {signStatus && (
            <Tag nowrap color={tagColor}>
              {t(signStatus)}
            </Tag>
          )}
        </NameWrapper>
        {company && <Chip size='small' label={company} />}
      </Content>
    </Wrapper>
  );
}
